// HistoriquePDF.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Download, 
  FileText, 
  Calendar, 
  User, 
  Archive,
  Trash2,
  Search,
  Filter,
  RefreshCw,
  Eye,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader
} from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const HistoriquePDF = ({ onClose, outils = [], ateliers = [], emplacements = [] }) => {
  const [historique, setHistorique] = useState([]);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isVisible, setIsVisible] = useState(true);

  // Charger l'historique
  const loadHistorique = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('mat_historique_pdf')
        .select(`
          *,
          emplacement:mat_emplacements(
            id,
            nom,
            photo_armoire_url,
            atelier:mat_ateliers(id, code, nom)
          )
        `)
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      setHistorique(data || []);
    } catch (error) {
      console.error('Erreur chargement historique:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHistorique();
  }, []);

  // Filtrer l'historique
  const historiqueFiltré = useMemo(() => {
    if (!Array.isArray(historique)) return [];
    
    let filtered = historique;

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(h => 
        h?.nom_fichier?.toLowerCase().includes(search) ||
        h?.emplacement?.nom?.toLowerCase().includes(search) ||
        h?.metadata?.atelier?.toLowerCase().includes(search)
      );
    }

    if (filterType !== 'all') {
      filtered = filtered.filter(h => h?.type_document === filterType);
    }

    return filtered;
  }, [historique, searchTerm, filterType]);

  // Fonction de fermeture robuste
  const handleClose = () => {
    if (onClose && typeof onClose === 'function') {
      onClose();
    } else {
      setIsVisible(false);
    }
  };

  // Charger une image en base64
  const loadImage = async (url) => {
    if (!url) return null;
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Erreur chargement image:', error);
      return null;
    }
  };

  // Régénérer et télécharger un PDF
  const regenererPDF = async (record) => {
    setRegenerating(record.id);
    
    try {
      const metadata = record.metadata || {};
      const atelierFilter = metadata.atelier || 'all';
      const emplacementFilter = metadata.emplacement || 'all';
      const includePhotos = metadata.include_photos || false;
      const includeArmoirePhoto = metadata.include_armoire_photo || false;

      let outilsFiltres = [...outils];
      
      if (atelierFilter !== 'all') {
        outilsFiltres = outilsFiltres.filter(o => o?.atelier_code === atelierFilter);
      }
      
      if (emplacementFilter !== 'all') {
        outilsFiltres = outilsFiltres.filter(o => o?.emplacement_nom === emplacementFilter);
      }

      if (metadata.outils_ids && Array.isArray(metadata.outils_ids)) {
        const outilsParId = outils.filter(o => metadata.outils_ids.includes(o?.id));
        if (outilsParId.length > 0) {
          outilsFiltres = outilsParId;
        }
      }

      let armoirePhotoData = null;
      if (includeArmoirePhoto && record.emplacement?.photo_armoire_url) {
        armoirePhotoData = await loadImage(record.emplacement.photo_armoire_url);
      }

      const hasArmoirePhoto = armoirePhotoData !== null;
      
      const doc = new jsPDF({
        orientation: hasArmoirePhoto ? 'portrait' : 'landscape',
        unit: 'mm',
        format: 'a3'
      });

      if (hasArmoirePhoto && record.emplacement) {
        const pageWidth = 297;
        const pageHeight = 420;
        
        doc.setFontSize(24);
        doc.setFont('helvetica', 'bold');
        doc.text('INVENTAIRE ARMOIRE', pageWidth / 2, 25, { align: 'center' });
        
        doc.setFontSize(18);
        doc.setFont('helvetica', 'normal');
        doc.text(record.emplacement.nom || '', pageWidth / 2, 38, { align: 'center' });
        
        doc.setFontSize(14);
        const atelierText = record.emplacement.atelier ? 
          `Atelier : ${record.emplacement.atelier.code} - ${record.emplacement.atelier.nom}` : '';
        doc.text(atelierText, pageWidth / 2, 50, { align: 'center' });
        
        doc.setFontSize(12);
        doc.text(`Regenere le ${new Date().toLocaleDateString('fr-FR')}`, pageWidth / 2, 60, { align: 'center' });
        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text(`(Original: ${new Date(record.created_at).toLocaleDateString('fr-FR')})`, pageWidth / 2, 67, { align: 'center' });
        doc.setTextColor(0);
        
        doc.setLineWidth(0.5);
        doc.setDrawColor(100, 100, 100);
        doc.line(20, 72, pageWidth - 20, 72);
        
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text("VUE D'ENSEMBLE DE L'ARMOIRE", pageWidth / 2, 86, { align: 'center' });
        
        const maxPhotoWidth = pageWidth - 40;
        const maxPhotoHeight = pageHeight - 140;
        
        const img = new Image();
        img.src = armoirePhotoData;
        
        await new Promise((resolve) => {
          img.onload = () => {
            const imgRatio = img.width / img.height;
            
            let photoWidth, photoHeight;
            
            if (imgRatio > maxPhotoWidth / maxPhotoHeight) {
              photoWidth = maxPhotoWidth;
              photoHeight = maxPhotoWidth / imgRatio;
            } else {
              photoHeight = maxPhotoHeight;
              photoWidth = maxPhotoHeight * imgRatio;
            }
            
            const photoX = (pageWidth - photoWidth) / 2;
            const photoY = 95;
            
            doc.setDrawColor(59, 130, 246);
            doc.setLineWidth(2);
            doc.rect(photoX - 3, photoY - 3, photoWidth + 6, photoHeight + 6);
            
            doc.addImage(armoirePhotoData, 'JPEG', photoX, photoY, photoWidth, photoHeight);
            
            doc.setFontSize(10);
            doc.setFont('helvetica', 'italic');
            doc.setTextColor(100);
            doc.text(
              `Photo de l'armoire "${record.emplacement.nom}" - ${outilsFiltres.length} outil(s)`,
              pageWidth / 2,
              photoY + photoHeight + 15,
              { align: 'center' }
            );
            doc.setTextColor(0);
            
            resolve();
          };
          img.onerror = resolve;
        });
        
        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text("Page 1 - Vue d'ensemble", pageWidth / 2, pageHeight - 15, { align: 'center' });
        doc.setTextColor(0);
        
        doc.addPage('a3', 'landscape');
      }

      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text('INVENTAIRE MATERIEL - CFA MONTARGIS', 20, 20);

      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      let subtitle = '';
      if (atelierFilter !== 'all') subtitle += `Atelier: ${atelierFilter}`;
      if (emplacementFilter !== 'all') subtitle += subtitle ? ` | Armoire: ${emplacementFilter}` : `Armoire: ${emplacementFilter}`;
      if (subtitle) doc.text(subtitle, 20, 27);

      doc.setFontSize(10);
      doc.text(`Regenere le ${new Date().toLocaleDateString('fr-FR')} (Original: ${new Date(record.created_at).toLocaleDateString('fr-FR')})`, 20, subtitle ? 33 : 27);

      const startY = subtitle ? 40 : 35;

      let outilsAvecPhotos = outilsFiltres;
      
      if (includePhotos) {
        outilsAvecPhotos = await Promise.all(
          outilsFiltres.map(async (outil) => {
            let photoData = null;
            if (outil?.photo_url) {
              photoData = await loadImage(outil.photo_url);
            }
            return { ...outil, photoData };
          })
        );
      }

      if (includePhotos) {
        const tableData = outilsAvecPhotos.map(outil => [
          outil?.nom || 'N/A',
          outil?.reference || 'N/A',
          outil?.categorie_nom || 'N/A',
          `${outil?.atelier_code || ''} - ${outil?.emplacement_nom || ''}`,
          String(outil?.quantite || 0),
          outil?.etat === 'complet' ? 'Complet' : 'Incomplet',
          outil?.disponible ? 'Dispo' : 'Emprunte',
          ''
        ]);

        autoTable(doc, {
          head: [['Nom', 'Ref', 'Categorie', 'Emplacement', 'Qte', 'Etat', 'Statut', 'Photo']],
          body: tableData,
          startY: startY,
          styles: { fontSize: 8, cellPadding: 2, minCellHeight: 15 },
          headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold', halign: 'center' },
          alternateRowStyles: { fillColor: [248, 250, 252] },
          columnStyles: {
            0: { cellWidth: 60 },
            1: { cellWidth: 30 },
            2: { cellWidth: 40 },
            3: { cellWidth: 60 },
            4: { cellWidth: 15, halign: 'center' },
            5: { cellWidth: 25, halign: 'center' },
            6: { cellWidth: 20, halign: 'center' },
            7: { cellWidth: 25, halign: 'center' }
          },
          didDrawCell: (data) => {
            if (data.column.index === 7 && data.cell.section === 'body') {
              const outil = outilsAvecPhotos[data.row.index];
              if (outil?.photoData) {
                try {
                  const photoWidth = 20;
                  const photoHeight = 12;
                  const x = data.cell.x + (data.cell.width - photoWidth) / 2;
                  const y = data.cell.y + (data.cell.height - photoHeight) / 2;
                  doc.addImage(outil.photoData, 'JPEG', x, y, photoWidth, photoHeight);
                } catch (error) {
                  doc.setFontSize(6);
                  doc.setTextColor(150);
                  doc.text('N/A', data.cell.x + data.cell.width / 2, data.cell.y + data.cell.height / 2, { align: 'center' });
                  doc.setTextColor(0);
                }
              }
            }
          }
        });
      } else {
        const tableData = outilsFiltres.map(outil => [
          outil?.nom || 'N/A',
          outil?.reference || 'N/A',
          outil?.categorie_nom || 'N/A',
          `${outil?.atelier_code || ''} - ${outil?.emplacement_nom || ''}`,
          String(outil?.quantite || 0),
          outil?.etat === 'complet' ? 'Complet' : 'Incomplet',
          outil?.disponible ? 'Disponible' : 'Emprunte'
        ]);

        autoTable(doc, {
          head: [['Nom', 'Reference', 'Categorie', 'Emplacement', 'Qte', 'Etat', 'Statut']],
          body: tableData,
          startY: startY,
          styles: { fontSize: 9, cellPadding: 3 },
          headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
          alternateRowStyles: { fillColor: [248, 250, 252] },
          columnStyles: {
            0: { cellWidth: 80 },
            1: { cellWidth: 40 },
            2: { cellWidth: 50 },
            3: { cellWidth: 70 },
            4: { cellWidth: 20, halign: 'center' },
            5: { cellWidth: 30, halign: 'center' },
            6: { cellWidth: 30, halign: 'center' }
          }
        });
      }

      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        const pageSize = doc.internal.pageSize;
        doc.setFontSize(8);
        doc.setTextColor(150);
        if (!(hasArmoirePhoto && i === 1)) {
          doc.text(`Page ${i} sur ${pageCount}`, pageSize.getWidth() / 2, pageSize.getHeight() - 10, { align: 'center' });
        }
      }

      const fileName = record.nom_fichier || `Inventaire_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);

    } catch (error) {
      console.error('Erreur régénération PDF:', error);
      alert('Erreur lors de la régénération du PDF: ' + error.message);
    } finally {
      setRegenerating(null);
    }
  };

  const supprimerEnregistrement = async (id) => {
    try {
      const { error } = await supabase
        .from('mat_historique_pdf')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setHistorique(prev => prev.filter(h => h.id !== id));
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTypeLabel = (type) => {
    const types = {
      'inventaire': 'Inventaire simple',
      'inventaire_complet': 'Inventaire complet',
      'photo_armoire': 'Photo armoire'
    };
    return types[type] || type || 'Inconnu';
  };

  const getTypeBadgeColor = (type) => {
    const colors = {
      'inventaire': 'bg-blue-500/20 text-blue-400 border-blue-500/50',
      'inventaire_complet': 'bg-purple-500/20 text-purple-400 border-purple-500/50',
      'photo_armoire': 'bg-green-500/20 text-green-400 border-green-500/50'
    };
    return colors[type] || 'bg-gray-500/20 text-gray-400 border-gray-500/50';
  };

  // Le return conditionnel APRÈS tous les hooks
  if (!isVisible) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
      onClick={handleClose}
    >
      <div
        className="bg-slate-900 rounded-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-slate-800 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Clock className="w-7 h-7 text-blue-400" />
            Historique des PDF
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={loadHistorique}
              className="p-2 text-gray-400 hover:text-white hover:bg-slate-700 rounded-lg transition-all"
              title="Rafraîchir"
              type="button"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleClose();
              }}
              className="p-2 text-gray-400 hover:text-white hover:bg-slate-700 rounded-lg transition-all cursor-pointer"
              type="button"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Filtres */}
        <div className="px-6 py-4 border-b border-slate-700 flex flex-wrap gap-4">
          <div className="flex-1 min-w-64">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher par nom, emplacement..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-10 pr-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-400" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Tous les types</option>
              <option value="inventaire">Inventaire simple</option>
              <option value="inventaire_complet">Inventaire complet</option>
              <option value="photo_armoire">Photo armoire</option>
            </select>
          </div>
        </div>

        {/* Liste */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="w-8 h-8 text-blue-400 animate-spin" />
              <span className="ml-3 text-gray-400">Chargement de l'historique...</span>
            </div>
          ) : historiqueFiltré.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">Aucun PDF dans l'historique</p>
              <p className="text-gray-500 text-sm mt-2">
                Les PDF générés apparaîtront ici
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {historiqueFiltré.map((record) => (
                <div
                  key={record.id}
                  className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 hover:bg-slate-800 transition-all"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1">
                      <div className="p-3 bg-blue-500/20 rounded-lg">
                        <FileText className="w-6 h-6 text-blue-400" />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-white font-semibold truncate">
                            {record.nom_fichier || 'Document sans nom'}
                          </h3>
                          <span className={`px-2 py-0.5 text-xs rounded-full border ${getTypeBadgeColor(record.type_document)}`}>
                            {getTypeLabel(record.type_document)}
                          </span>
                          {record.est_valide ? (
                            <CheckCircle className="w-4 h-4 text-green-400" />
                          ) : (
                            <AlertCircle className="w-4 h-4 text-orange-400" />
                          )}
                        </div>
                        
                        <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-400">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {formatDate(record.created_at)}
                          </div>
                          
                          {record.emplacement && (
                            <div className="flex items-center gap-1">
                              <Archive className="w-4 h-4" />
                              {record.emplacement.nom}
                            </div>
                          )}
                          
                          {record.metadata?.atelier && record.metadata.atelier !== 'all' && (
                            <div className="flex items-center gap-1">
                              <span className="text-purple-400">{record.metadata.atelier}</span>
                            </div>
                          )}
                          
                          {record.metadata?.nombre_outils && (
                            <div className="text-blue-400">
                              {record.metadata.nombre_outils} outil(s)
                            </div>
                          )}
                          
                          {record.metadata?.genere_par && (
                            <div className="flex items-center gap-1">
                              <User className="w-4 h-4" />
                              {record.metadata.genere_par}
                            </div>
                          )}
                        </div>

                        <div className="flex gap-2 mt-2">
                          {record.metadata?.include_photos && (
                            <span className="px-2 py-0.5 text-xs bg-green-500/20 text-green-400 rounded">
                              Photos outils
                            </span>
                          )}
                          {record.metadata?.include_armoire_photo && (
                            <span className="px-2 py-0.5 text-xs bg-purple-500/20 text-purple-400 rounded">
                              Photo armoire
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => regenererPDF(record)}
                        disabled={regenerating === record.id}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all disabled:opacity-50"
                        title="Régénérer et télécharger"
                        type="button"
                      >
                        {regenerating === record.id ? (
                          <Loader className="w-4 h-4 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                        <span className="hidden sm:inline">
                          {regenerating === record.id ? 'Génération...' : 'Télécharger'}
                        </span>
                      </button>
                      
                      <button
                        onClick={() => setDeleteConfirm(record.id)}
                        className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-lg transition-all"
                        title="Supprimer de l'historique"
                        type="button"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {deleteConfirm === record.id && (
                    <div className="mt-4 pt-4 border-t border-slate-600">
                      <div className="flex items-center justify-between">
                        <p className="text-orange-400 text-sm">
                          Supprimer cet enregistrement de l'historique ?
                        </p>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setDeleteConfirm(null)}
                            className="px-3 py-1 text-gray-400 hover:text-white transition-colors"
                            type="button"
                          >
                            Annuler
                          </button>
                          <button
                            onClick={() => supprimerEnregistrement(record.id)}
                            className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded transition-all"
                            type="button"
                          >
                            Supprimer
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer stats */}
        <div className="px-6 py-3 bg-slate-800 border-t border-slate-700 flex items-center justify-between text-sm text-gray-400">
          <span>
            {historiqueFiltré.length} document(s) trouvé(s)
          </span>
          <span>
            Total: {historique.length} dans l'historique
          </span>
        </div>
      </div>
    </div>
  );
};

export default HistoriquePDF;